import { BsCurrencyDollar } from 'react-icons/bs';
import { ImLocation } from 'react-icons/im';
import { HiOutlineCalendar } from 'react-icons/hi';
import defaultAvatar from '@assets/default-avatar.png';
import {
  Anchor,
  Avatar,
  Badge,
  Box,
  Button,
  Flex,
  Rating,
  Stack,
  Text,
  Title,
  Tooltip,
} from '@mantine/core';
import { isMobile } from 'react-device-detect';
import {
  JobMutationAction,
  JobMutationState,
  MyJobsTabsEnum,
  TransformedJobItem,
} from '@worker/types/myJobs.type';
import JobActionsToolbar from '@worker/components/MyJobs/JobActionsToolbar/JobActionsToolbar';
import {
  buildJobViewUrl,
  getRecommendationFlags,
} from '@worker/helpers/my-jobs-helpers';
import { formatLocalDate } from '@utils/DateUtils';
import DATE_FORMATS from '@constants/dateFormat';
import { FiMapPin, FiUser } from 'react-icons/fi';
import { getGoogleMapsUrl } from '@utils/JobUtils';
import { useWorkerStore } from '@worker/stores';

export interface JobListItemProps {
  recommendation: TransformedJobItem;
  onDismiss: () => void;
  onBookmark: () => void;
  onShare: () => void;
  onMessage: () => void;
  onApply: (recommendation: TransformedJobItem) => void;
  onUnApply: (recommendation: TransformedJobItem) => void;
  onViewDetails: (recommendation: TransformedJobItem) => void;
  /** Unified mutation state for loading indicators */
  mutation: JobMutationState;
}

function JobListItem({
  recommendation,
  onDismiss,
  onBookmark,
  onShare,
  onMessage,
  onApply,
  onUnApply,
  onViewDetails,
  mutation,
}: JobListItemProps) {
  const { distance, canApply, canUnApply } = recommendation;
  const currentTab = useWorkerStore((state) => state.currentTab);

  const { job, isOffered, isHired, isPastJob, isCancelled } =
    getRecommendationFlags(recommendation);

  if (!job) {
    return null;
  }

  const companyProfileUrl = job.company.profileURLName
    ? `${import.meta.env.VITE_SERVER_BASE_URL}/companies/${job.company.profileURLName}`
    : undefined;

  const handleViewJobPage = () => {
    const url = buildJobViewUrl(recommendation.jobAssignmentId, job.publicUrl);
    if (url) window.open(url, '_blank');
  };

  const showDismiss = currentTab === MyJobsTabsEnum.NEW;

  const renderActionIcons = (spread = false) => (
    <JobActionsToolbar
      recommendation={recommendation}
      mutation={mutation}
      onDismiss={showDismiss ? onDismiss : undefined}
      onBookmark={onBookmark}
      onShare={onShare}
      onMessage={onMessage}
      isPastJob={isPastJob}
      spread={spread}
    />
  );

  const renderCompanyInfo = () => {
    const rating = job.company.rating || 0;
    const reviewsCount = job.company.reviewsCount || 0;

    return (
      <Flex gap="xs" align="center">
        <Anchor
          href={companyProfileUrl}
          target="_blank"
          rel="noopener noreferrer"
          c="inherit"
        >
          {job.company.name}
        </Anchor>
        <Rating
          size="24"
          value={rating}
          readOnly
          fractions={2}
          color="var(--yellow)"
        />
        {!isMobile && <Text>{rating.toFixed(1)} Overall</Text>}
        {!isMobile && <Text c="dimmed">({reviewsCount} reviews)</Text>}
      </Flex>
    );
  };

  const renderJobDetails = () => {
    const payRateText = `${job.payRate.toFixed(2)} / ${job.payRatePeriod.toLowerCase()}`;

    const getDistanceAndJobAddressText = () => {
      if (job.nationalJob) {
        return 'National Job';
      }
      return job.address?.googleAddress
        ? `${distance && distance >= 0 ? `${distance.toFixed(1)} miles - ` : ''} ${job.address?.googleAddress}`
        : '';
    };

    return (
      <>
        <Flex align="center" gap="4" style={{ flexShrink: 0 }}>
          <HiOutlineCalendar size={20} />
          <Text style={{ whiteSpace: 'nowrap' }}>
            {`${formatLocalDate(new Date(job.startDate), DATE_FORMATS.DATE_FORMAT)} - ${formatLocalDate(new Date(job.endDate), DATE_FORMATS.DATE_FORMAT)}`}
          </Text>
        </Flex>
        {!isMobile && job.openPositions && (
          <Flex align="center" gap="4" style={{ flexShrink: 0 }}>
            <FiUser size={20} />
            <Text>{job.openPositions}</Text>
          </Flex>
        )}
        <Flex align="center" gap="4" style={{ flexShrink: 0 }}>
          <BsCurrencyDollar size={20} />
          <Text style={{ whiteSpace: 'nowrap' }}>{payRateText}</Text>
        </Flex>
        <Flex align="center" gap="4" style={{ minWidth: 0 }}>
          <ImLocation size={16} style={{ flexShrink: 0 }} />
          {!job.nationalJob && job.address?.googleAddress ? (
            <Tooltip label={job.address.googleAddress} multiline maw={400}>
              <Anchor
                href={getGoogleMapsUrl(job.address.googleAddress)}
                target="_blank"
                rel="noopener noreferrer"
                truncate
                style={{ maxWidth: 500, color: 'inherit' }}
                size="sm"
                underline="never"
              >
                {getDistanceAndJobAddressText()}
              </Anchor>
            </Tooltip>
          ) : (
            <Tooltip
              label={getDistanceAndJobAddressText()}
              multiline
              maw={400}
              disabled={!getDistanceAndJobAddressText()}
            >
              <Text truncate style={{ maxWidth: 500 }}>
                {getDistanceAndJobAddressText()}
              </Text>
            </Tooltip>
          )}
        </Flex>
      </>
    );
  };

  const isApplyingOrUnapplying =
    (mutation.action === JobMutationAction.APPLY ||
      mutation.action === JobMutationAction.UNAPPLY) &&
    mutation.targetId === job.id;

  const getActionButtonConfig = () => {
    if (isPastJob) {
      return { text: 'Past Job', disabled: true };
    }
    if (isCancelled) {
      return {
        text: 'Canceled',
        variant: 'filled' as const,
        color: 'yellow',
        notClickable: true,
      };
    }
    if (isOffered || isHired) {
      return {
        text: isOffered ? 'View Offer' : 'View Job',
        onClick: handleViewJobPage,
        variant: 'filled' as const,
      };
    }
    if (canUnApply) {
      return {
        text: 'Un-Apply',
        onClick: () => onUnApply(recommendation),
        loading: isApplyingOrUnapplying,
        variant: 'outline' as const,
        color: 'red',
      };
    }
    if (canApply) {
      return {
        text: "I'm Interested",
        onClick: () => onApply(recommendation),
        loading: isApplyingOrUnapplying,
      };
    }
    return null;
  };

  const renderActionButton = () => {
    const config = getActionButtonConfig();
    if (!config) return null;

    const getDesktopVariant = () => {
      if (config.disabled) return 'light';
      if (config.variant === 'outline') return 'white';
      return config.variant || 'filled';
    };
    const variant = getDesktopVariant();

    const notClickableStyle = config.notClickable
      ? { cursor: 'not-allowed', opacity: 0.65 }
      : undefined;

    return (
      <Button
        variant={variant}
        color={config.color}
        size="md"
        tt="capitalize"
        bdrs="xs"
        w={150}
        disabled={config.disabled}
        loading={config.loading}
        onClick={config.onClick}
        style={notClickableStyle}
      >
        {config.text}
      </Button>
    );
  };

  const renderMobileActionButton = () => {
    const config = getActionButtonConfig();
    if (!config) return null;

    const variant = config.variant || 'outline';
    const color = config.color || (config.disabled ? undefined : 'dark');

    const notClickableStyle = config.notClickable
      ? { cursor: 'not-allowed', opacity: 0.65 }
      : undefined;

    return (
      <Button
        variant={variant}
        color={color}
        size="sm"
        tt="uppercase"
        fw={700}
        disabled={config.disabled}
        loading={config.loading}
        onClick={config.onClick}
        style={notClickableStyle}
      >
        {config.text}
      </Button>
    );
  };

  // Mobile layout
  if (isMobile) {
    const rating = job.company.rating || 0;
    const reviewsCount = job.company.reviewsCount || 0;
    const hasValidDistance =
      distance !== undefined && distance !== null && distance >= 0;
    const distanceText = hasValidDistance ? `${distance.toFixed(1)} miles` : '';
    const locationText = job.nationalJob
      ? 'National Job'
      : job.address?.googleAddress;

    return (
      <Box
        mb={10}
        bd="1px solid var(--border-color)"
        style={{
          boxShadow: '0 0 6px 0 var(--box-shadow)',
        }}
        bdrs="8px"
        p="sm"
      >
        {/* Header: Avatar + Title + Company Info */}
        <Flex align="flex-start" gap="sm">
          <Anchor
            href={companyProfileUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{ flexShrink: 0 }}
          >
            <Avatar
              w={80}
              h={80}
              src={job.company.profileImageUrl || defaultAvatar}
              radius="sm"
            />
          </Anchor>
          <Stack gap={2} style={{ flex: 1 }}>
            <Title order={5} fw={700}>
              {job.title}
              {job.brandRepresented && (
                <Text span fw={400} size="sm">
                  {' '}
                  - {job.brandRepresented}
                </Text>
              )}
            </Title>
            <Flex gap={4} align="center" wrap="wrap">
              <Anchor
                href={companyProfileUrl}
                target="_blank"
                rel="noopener noreferrer"
                size="sm"
                c="inherit"
              >
                {job.company.name}
              </Anchor>
              <Rating
                size="xs"
                value={rating}
                readOnly
                fractions={2}
                color="var(--yellow)"
              />
            </Flex>
            <Flex gap={4} align="center">
              <Text size="sm" fw={600}>
                {rating.toFixed(2)} Overall
              </Text>
              <Text size="sm" c="dimmed">
                ({reviewsCount} Reviews)
              </Text>
            </Flex>
          </Stack>
        </Flex>

        {/* Job Details */}
        <Stack gap={6} mt="sm">
          {/* Date + Applied Badge */}
          <Flex align="center" gap={6} wrap="wrap">
            <Flex align="center" gap={6} style={{ flex: 1, minWidth: 0 }}>
              <HiOutlineCalendar size={18} style={{ flexShrink: 0 }} />
              <Text size="sm">
                {formatLocalDate(
                  new Date(job.startDate),
                  DATE_FORMATS.DATE_FORMAT
                )}{' '}
                -{' '}
                {formatLocalDate(
                  new Date(job.endDate),
                  DATE_FORMATS.DATE_FORMAT
                )}
              </Text>
            </Flex>
            {canUnApply && (
              <Badge color="var(--yellow)" variant="filled" size="sm">
                APPLIED
              </Badge>
            )}
          </Flex>

          {/* Location with distance */}
          <Flex align="center" gap={6}>
            <FiMapPin size={18} style={{ flexShrink: 0 }} />
            {!job.nationalJob && job.address?.googleAddress ? (
              <Anchor
                href={getGoogleMapsUrl(job.address.googleAddress)}
                target="_blank"
                rel="noopener noreferrer"
                size="sm"
                style={{ wordBreak: 'break-word', color: 'inherit' }}
                underline="never"
              >
                {distanceText}
                {distanceText && locationText ? ' ' : ''}
                {locationText}
              </Anchor>
            ) : (
              <Text size="sm" style={{ wordBreak: 'break-word' }}>
                {distanceText}
                {distanceText && locationText ? ' ' : ''}
                {locationText}
              </Text>
            )}
          </Flex>

          {/* Pay Rate */}
          <Flex align="center" gap={6}>
            <BsCurrencyDollar size={18} style={{ flexShrink: 0 }} />
            <Text size="sm">
              {job.payRate.toFixed(2)} / {job.payRatePeriod.toLowerCase()}
            </Text>
          </Flex>
        </Stack>

        {/* Action Icons */}
        <Flex justify="space-around" mt="sm">
          {renderActionIcons(true)}
        </Flex>

        {/* Action Buttons - Always show View Details + Action Button */}
        <Flex gap="xs" mt="sm">
          <Button
            variant="filled"
            size="sm"
            tt="uppercase"
            fw={700}
            onClick={() => onViewDetails(recommendation)}
            style={{
              flex: 1,
              minWidth: 0,
              backgroundColor: 'var(--mantine-color-green-1)',
              color: 'var(--mantine-color-green-7)',
            }}
          >
            View Details
          </Button>
          {renderMobileActionButton()}
        </Flex>
      </Box>
    );
  }

  // Desktop layout
  return (
    <Flex
      display="flex"
      align="center"
      gap="md"
      p="xs"
      bd="1px solid var(--border-color)"
      bdrs="4px"
      style={{
        boxShadow: '0 0 6px 0 var(--box-shadow)',
      }}
    >
      <Anchor
        href={companyProfileUrl}
        target="_blank"
        rel="noopener noreferrer"
        style={{ flexShrink: 0 }}
      >
        <Avatar
          w={86}
          h={86}
          src={job.company.profileImageUrl || defaultAvatar}
          radius="sm"
        />
      </Anchor>
      <Stack justify="space-between" h="100%" style={{ flex: 1, minWidth: 0 }}>
        <Title order={4}>
          {job.title}
          {job.brandRepresented && (
            <Text span fw={400} size="md">
              {' '}
              - {job.brandRepresented}
            </Text>
          )}
        </Title>

        <Text size="14px">
          <Stack gap="4">
            {renderCompanyInfo()}
            <Flex gap="md" align="center" wrap="wrap" rowGap="xs">
              {renderJobDetails()}
            </Flex>
          </Stack>
        </Text>
      </Stack>
      <Flex gap="xs" ml="auto" align="center" style={{ flexShrink: 0 }}>
        {renderActionIcons()}
        {/* Always show View Details + Action Button */}
        <Button
          variant="white"
          size="md"
          tt="capitalize"
          onClick={() => onViewDetails(recommendation)}
          bdrs="xs"
        >
          View Details
        </Button>
        {renderActionButton()}
      </Flex>
    </Flex>
  );
}

export default JobListItem;
